import { useState } from "react";
import Head from "next/head";
import classNames from "classnames/bind";
import Modal from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import ZoomCross24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ZoomCross24PxOutlined";
import { ProductImages, getSortedGalleryItems } from "../utils";
import { Video } from "../../../types/prodcatBaseTypes";
import styles from "./CarouselGallery.module.scss";
import { Carousel, MediaItem } from "./components/Carousel/Carousel";
import { LineIndicator } from "../components/LineIndicator/LineIndicator";
import { dispatchGalleryMonetateQEvent } from "../components/Analytics/dispatchGalleryMonetateQEvent";
import { imageProfileGenerator } from "../../../utils/image/imageProfileGenerator";
import { SocialProofMessages } from "../components/SocialProofMessages/SocialProofMessages";

const cx = classNames.bind(styles);

type Props = {
  title: string;
  images: ProductImages;
  videos: Video[];
  sizes: string;
  hasSocialProofMessagesExperience?: boolean;
  socialProofMessagesProperties?: {
    productId: string;
    brand: string;
    availableToOrder: boolean;
    appId: string;
    withClose?: boolean;
  };
  taggstarDataTag?: boolean;
  hasImmediateStateUpdates?: boolean;
  hasTransparentThumbnails?: boolean;
  hasDeferredMonetateQEvents?: boolean;
  hasDeferredIntersectionObserverCallback?: boolean;
};

const Counter = ({
  fullscreen,
  selectedInd,
  itemCount,
}: {
  fullscreen?: boolean;
  selectedInd: number;
  itemCount: number;
}) => {
  return (
    <div
      className={cx(styles.counter, {
        "counter--fullscreen": fullscreen,
      })}
    >
      {selectedInd + 1} / {itemCount}
    </div>
  );
};

export const CarouselGalleryUI = ({
  title,
  images,
  videos,
  sizes,
  hasSocialProofMessagesExperience,
  socialProofMessagesProperties,
  taggstarDataTag = false,
  hasImmediateStateUpdates,
  hasTransparentThumbnails,
  hasDeferredMonetateQEvents,
  hasDeferredIntersectionObserverCallback,
}: Props) => {
  const [selectedInd, setSelectedInd] = useState(0);
  const [modalSelectedInd, setModalSelectedInd] = useState(0);
  const [zoomModalActive, setZoomModalActive] = useState(false);
  const outputImages = [images.primary, ...images.alternatives].map((image) => {
    return {
      type: "image",
      imageUrl: image.split("?")[0],
      title,
      thumbnailUrl: `${image}?wid=120&hei=160`,
    };
  }) as MediaItem[];

  const outputVideos = videos.map((video) => {
    const videoThumbnail = video.sources.find(
      (source) => source.key === "thumbnail",
    );

    const videoThumbnailBaseUrl = videoThumbnail ? videoThumbnail.url : "";
    const videoTumbnailUrlWithParams = `${
      videoThumbnailBaseUrl.split("?")[0]
    }?wid=120&hei=160`;

    return {
      type: "video",
      imageUrl: videoThumbnailBaseUrl.split("?")[0],
      thumbnailUrl: videoTumbnailUrlWithParams,
      videoUrl: video.sources.find((source) => source.key === "stream")?.url,
      posterUrl: video.sources.find((source) => source.key === "poster")?.url,
      title,
    };
  }) as MediaItem[];

  const mediaItems = getSortedGalleryItems<MediaItem>(
    outputImages,
    outputVideos,
  );

  const showZoomModal = () => {
    const currentImageIndex = outputImages.findIndex(
      ({ imageUrl }) => mediaItems[selectedInd]?.imageUrl === imageUrl,
    );
    setModalSelectedInd(currentImageIndex);
    setZoomModalActive(true);
    dispatchGalleryMonetateQEvent("ZoomIcon");
  };

  const closeZoomModal = () => {
    setZoomModalActive(false);
    dispatchGalleryMonetateQEvent("CloseModal", "imageModal");
  };

  const TaggstarDataAttr = taggstarDataTag
    ? { "data-taggstar-mobile": true }
    : {};

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={images.primary}
          imageSrcSet={imageProfileGenerator(images.primary)}
          imageSizes={sizes}
        />
      </Head>
      <Carousel
        mediaItems={mediaItems}
        selectedInd={selectedInd}
        setSelectedInd={setSelectedInd}
        imageSizes={sizes}
        hasImmediateStateUpdates={hasImmediateStateUpdates}
        hasTransparentThumbnails={hasTransparentThumbnails}
        hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
        hasDeferredIntersectionObserverCallback={
          hasDeferredIntersectionObserverCallback
        }
      >
        {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
          <SocialProofMessages {...socialProofMessagesProperties} />
        )}
        <div
          id="mainImage"
          className={cx("slick-list", styles.taggstarPlacement)}
          data-testid="gallery:taggstar"
          {...TaggstarDataAttr}
        />
        {mediaItems.length > 1 && (
          <Counter selectedInd={selectedInd} itemCount={mediaItems.length} />
        )}
        {mediaItems[selectedInd]?.type === "image" && (
          <button
            className={styles.zoomButton}
            onClick={showZoomModal}
            data-testid="gallery:zoom:button"
          >
            <ZoomCross24PxOutlinedIcon className={styles.zoomIcon} />
            Zoom
          </button>
        )}
      </Carousel>
      {zoomModalActive && (
        <Modal
          isOpen
          fullscreen
          onClose={closeZoomModal}
          className={styles.modalContainer}
          hasPadding={false}
          closeButtonClassName={styles.closeIcon}
        >
          <Carousel
            fullscreen
            mediaItems={outputImages}
            selectedInd={modalSelectedInd}
            setSelectedInd={setModalSelectedInd}
            hasImmediateStateUpdates={hasImmediateStateUpdates}
            hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
            hasDeferredIntersectionObserverCallback={
              hasDeferredIntersectionObserverCallback
            }
          >
            {mediaItems.length > 1 && (
              <div className={styles.lineIndicator}>
                <LineIndicator
                  currentIndex={modalSelectedInd}
                  numberOfImages={outputImages.length}
                />
                <Counter
                  fullscreen
                  selectedInd={modalSelectedInd}
                  itemCount={outputImages.length}
                />
              </div>
            )}
          </Carousel>
        </Modal>
      )}
    </>
  );
};
